/**
 * ModeratorLoginPage.jsx  ·  Studio — Страница авторизации
 *
 * Полностью перестилизована под дизайн ModeratorPanel.jsx
 * (neumorphic, те же палитры D/T, Inter + JetBrains Mono).
 *
 * После успешного входа → перенаправляет на /{adminRoute}/stats
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import logoImg from "../../assets/1111.png";

// ── Палитры — синхронизированы с ModeratorPanel.jsx ────────────────────────
const D = {
  bg:         "#0d0c0f",
  bgDeep:     "#080709",
  card:       "#131117",
  primary:    "#b06090",
  accent:     "#d48ab0",
  red:        "#e8514a",
  redDim:     "rgba(232,81,74,0.12)",
  fg:         "#d8cee0",
  fgSub:      "#52445e",
  fgFaint:    "#2e2836",
  fgMid:      "#8a7a96",
  borderMid:  "rgba(180,120,200,0.18)",
  gradient:   "linear-gradient(135deg, #5c2050 0%, #8a3570 100%)",
};

const T = {
  bg:         "#FFF3E6",
  bgDeep:     "#FFE8D0",
  card:       "#ffffff",
  primary:    "#381931",
  accent:     "#6b2e58",
  red:        "#c0392b",
  redDim:     "rgba(192,57,43,0.09)",
  fg:         "#1a0c14",
  fgSub:      "#8a6070",
  fgFaint:    "#c0a0b0",
  fgMid:      "#4a2838",
  borderMid:  "#E0C4A8",
  gradient:   "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
};

function injectLoginCSS(c, isDark) {
  const neuShadow = isDark
    ? "8px 8px 20px rgba(0,0,0,0.55), -4px -4px 12px rgba(255,255,255,0.03)"
    : "8px 8px 20px rgba(163,177,198,0.5), -6px -6px 16px rgba(255,255,255,0.85)";
  const neuShadowSm = isDark
    ? "4px 4px 10px rgba(0,0,0,0.5), -2px -2px 6px rgba(255,255,255,0.03)"
    : "4px 4px 10px rgba(163,177,198,0.45), -3px -3px 8px rgba(255,255,255,0.8)";
  const neuInset = isDark
    ? "inset 3px 3px 8px rgba(0,0,0,0.5), inset -2px -2px 6px rgba(255,255,255,0.03)"
    : "inset 3px 3px 8px rgba(163,177,198,0.35), inset -3px -3px 8px rgba(255,255,255,0.75)";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    .sl-wrap{font-family:'Inter',sans-serif;min-height:100vh;background:${c.bg};display:flex;align-items:center;justify-content:center;padding:24px;transition:background .3s}
    @keyframes slFade{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slSpin{to{transform:rotate(360deg)}}
    .sl-fade{animation:slFade .28s cubic-bezier(.32,0,.67,0) both}
    .sl-card{background:${c.card};border-radius:24px;padding:36px 32px 28px;box-shadow:${neuShadow};transition:background .3s}
    .sl-logo-pill{display:inline-flex;align-items:center;gap:10px;padding:10px 20px;border-radius:18px;background:${c.bgDeep};box-shadow:${neuInset}}
    .sl-input{width:100%;padding:11px 14px;border-radius:12px;outline:none;font-size:14px;font-family:'Inter',sans-serif;background:${c.bgDeep};border:none;color:${c.fg};box-shadow:${neuInset};transition:box-shadow .15s,background .3s,color .3s;box-sizing:border-box}
    .sl-input:focus{box-shadow:${neuInset},0 0 0 2px ${c.primary}55}
    .sl-input::placeholder{color:${c.fgFaint}}
    .sl-label{font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:${c.fgSub};margin-bottom:7px;display:block}
    .sl-btn{width:100%;padding:13px;border:none;border-radius:14px;font-size:14px;font-weight:700;font-family:'Inter',sans-serif;letter-spacing:.02em;cursor:pointer;background:${c.gradient};color:#fff;display:flex;align-items:center;justify-content:center;gap:9px;box-shadow:${neuShadowSm};transition:filter .15s,transform .1s,box-shadow .1s}
    .sl-btn:hover:not(:disabled){filter:brightness(1.1);transform:translateY(-1px)}
    .sl-btn:active:not(:disabled){transform:translateY(0);filter:brightness(.97)}
    .sl-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none}
    .sl-spin{width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,.25);border-top-color:#fff;animation:slSpin .65s linear infinite;flex-shrink:0}
    .sl-err{background:${c.redDim};border-radius:10px;padding:10px 14px;color:${c.red};font-size:13px;font-weight:500}
    .sl-theme-btn{position:fixed;top:18px;right:18px;background:${c.card};border:none;border-radius:12px;padding:8px 16px;font-size:12px;font-weight:700;font-family:'Inter',sans-serif;color:${c.fgSub};cursor:pointer;box-shadow:${neuShadowSm};transition:all .15s;letter-spacing:.04em}
    .sl-theme-btn:hover{color:${c.fg}}
    .sl-divider{height:1px;background:linear-gradient(90deg,transparent,${c.borderMid},transparent);margin:18px 0}
    .sl-version{font-family:'JetBrains Mono',monospace;font-size:10px;color:${c.fgFaint};font-weight:500;letter-spacing:.08em;text-transform:uppercase;text-align:center;margin-top:18px}
  `;
  let el = document.getElementById("studio-login-css");
  if (!el) { el = document.createElement("style"); el.id = "studio-login-css"; document.head.appendChild(el); }
  el.textContent = css;
}

async function loginRequest(userId, password) {
  const r = await fetch(`${API_URL}/api/mod/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: Number(userId), password }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

export default function ModeratorLoginPage({ adminRoute }) {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem("studio_theme") || "dark");
  const [uid, setUid]     = useState("");
  const [pw, setPw]       = useState("");
  const [err, setErr]     = useState("");
  const [loading, setLoading] = useState(false);

  const isDark = theme === "dark";
  const c = isDark ? D : T;

  useEffect(() => { injectLoginCSS(c, isDark); }, [theme]);

  useEffect(() => {
    const stored = sessionStorage.getItem("studio_token");
    if (stored && adminRoute) navigate(`/${adminRoute}/stats`, { replace: true });
  }, [adminRoute, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const d = await loginRequest(uid, pw);
      sessionStorage.setItem("studio_token", d.access_token);
      sessionStorage.setItem("studio_name", d.name || "");
      navigate(`/${adminRoute}/stats`, { replace: true });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleTheme() {
    const next = isDark ? "light" : "dark";
    localStorage.setItem("studio_theme", next);
    setTheme(next);
  }

  return (
    <div className="sl-wrap">

      <button className="sl-theme-btn" onClick={toggleTheme}>
        {isDark ? "☀ Light" : "☾ Dark"}
      </button>

      <div className="sl-fade" style={{ width: "100%", maxWidth: 400 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
            <div className="sl-logo-pill">
              <img src={logoImg} alt="Logo" style={{ height: 36, width: "auto" }} />
            </div>
          </div>
          <div style={{
            fontSize: 28, fontWeight: 700, lineHeight: 1.15,
            background: c.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: 6,
          }}>
            Admin Panel
          </div>
          <div style={{ fontSize: 12, color: c.fgSub, fontWeight: 500, letterSpacing: ".06em" }}>
            Sign in to your account
          </div>
        </div>

        {/* Card */}
        <div className="sl-card">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <label>
              <span className="sl-label">User ID</span>
              <input
                className="sl-input"
                type="number"
                placeholder="12345"
                value={uid}
                onChange={e => setUid(e.target.value)}
                autoFocus
                required
              />
            </label>

            <label>
              <span className="sl-label">Password</span>
              <input
                className="sl-input"
                type="password"
                placeholder="••••••••"
                value={pw}
                onChange={e => setPw(e.target.value)}
                required
              />
            </label>

            {err && <div className="sl-err">✕ {err}</div>}

            <div className="sl-divider" />

            <button className="sl-btn" type="submit" disabled={loading}>
              {loading
                ? <><div className="sl-spin" /> Signing in…</>
                : "Sign in to DolbaibikiPanel"
              }
            </button>

          </form>

          <div className="sl-version">DolbaibikiPanel v0.1</div>
        </div>

      </div>
    </div>
  );
}